/**
 * Batch-1 §1.2 + §1.7 — la péremption d'achat : saisie `MM/AA` et « sans péremption ».
 *
 * Deux règles, toutes deux écrites ici plutôt que déduites au point d'usage :
 *
 *  §1.2  UNE BOÎTE MARQUÉE 01/26 EST BONNE TOUT JANVIER. `MM/AA` désigne donc le
 *        DERNIER jour du mois, jamais le premier. C'est la convention
 *        pharmaceutique, et l'inverse retirerait un mois entier de vie à chaque
 *        lot reçu.
 *
 *  §1.7  UNE PÉREMPTION N'EST OBLIGATOIRE QUE POUR UN MÉDICAMENT OU UNE
 *        PARAPHARMACIE. Les autres types entrent en stock avec la SENTINELLE
 *        « sans péremption » — une date volontairement très lointaine plutôt
 *        qu'un NULL, pour que la chaîne FEFO (qui trie sur cette colonne) reste
 *        intacte : un tri croissant range naturellement la sentinelle EN
 *        DERNIER, ce qui est exactement le `NULLS LAST` recherché.
 *
 * Jumeau serveur : `apps/server/src/config/achat-secours.config.js`. Les deux
 * fichiers portent les mêmes valeurs ; les tests de ce module figent le
 * contrat.
 */

// ─── La sentinelle « sans péremption » ───────────────────────────────────────

/** Ce que l'on envoie au serveur (le validateur exige `YYYY-MM-DD`). */
export const PEREMPTION_SENTINELLE = "9999-12-31";

/** Au-delà de ce seuil, la date n'est pas une péremption : c'est la sentinelle. */
export const PEREMPTION_SENTINELLE_SEUIL = "9990-01-01";

/** Ce que l'interface affiche à la place de la sentinelle. */
export const PEREMPTION_SANS_LIBELLE = "Sans péremption";

/** Ce que la cellule affiche quand la ligne n'exige pas de péremption. */
export const PEREMPTION_SANS_PLACEHOLDER = "—";

/** Types de produit pour lesquels la péremption reste OBLIGATOIRE. */
export const PEREMPTION_REQUISE_POUR = Object.freeze([
  "MEDICAMENT",
  "PARAPHARMACIE",
]);

/**
 * @param {string|null|undefined} typeProduit
 * @returns {boolean} vrai si la ligne doit porter une vraie péremption.
 *          Type inconnu ⇒ on exige la date (le choix prudent : mieux vaut
 *          demander une date de trop que laisser entrer un médicament sans).
 */
export function peremptionRequisePour(typeProduit) {
  if (!typeProduit) return true;
  return PEREMPTION_REQUISE_POUR.includes(String(typeProduit).toUpperCase());
}

/**
 * @param {string|Date|null|undefined} v  date ISO ou Date
 * @returns {boolean} vrai si c'est la sentinelle « sans péremption ».
 */
export function estPeremptionSentinelle(v) {
  if (!v) return false;
  const s = v instanceof Date ? v.toISOString().slice(0, 10) : String(v).slice(0, 10);
  return s >= PEREMPTION_SENTINELLE_SEUIL;
}

// ─── §1.2 — le masque MM/AA ──────────────────────────────────────────────────

/**
 * Masque de saisie : chiffres seuls, le `/` s'insère tout seul après deux
 * chiffres. `0126`, `01/26` et `012026` donnent tous « 01/26 ».
 *
 * @param {string} raw  ce que l'opérateur vient de taper
 * @returns {string}    la valeur affichée dans la cellule
 */
export function maskMMAA(raw) {
  const digits = String(raw ?? "").replace(/\D/g, "").slice(0, 6);
  if (digits.length <= 2) return digits;
  // 4-digit year typed in full (012026) → keep the last two.
  const mm = digits.slice(0, 2);
  const rest = digits.slice(2);
  const aa = rest.length >= 4 ? rest.slice(2, 4) : rest.slice(0, 2);
  return `${mm}/${aa}`;
}

/**
 * `MM/AA` → date ISO `YYYY-MM-DD` du DERNIER jour du mois (§1.2).
 *
 * Le siècle : `26` → 2026. On prend systématiquement 20xx — une péremption
 * d'achat est toujours à venir, et 19xx serait toujours faux.
 *
 * @param {string} mmaa
 * @returns {string} `YYYY-MM-DD`, ou "" si la saisie est incomplète/invalide.
 */
export function mmaaToIso(mmaa) {
  const digits = String(mmaa ?? "").replace(/\D/g, "");
  if (digits.length < 4) return "";
  const mm = parseInt(digits.slice(0, 2), 10);
  if (!(mm >= 1 && mm <= 12)) return "";
  const rest = digits.slice(2);
  const aa = rest.length >= 4 ? parseInt(rest.slice(0, 4), 10) : 2000 + parseInt(rest.slice(0, 2), 10);
  if (!Number.isFinite(aa) || aa < 2000 || aa > 2999) return "";
  // Day 0 of the NEXT month == the last day of THIS one. Handles February and
  // leap years without a table.
  const dernier = new Date(Date.UTC(aa, mm, 0));
  return dernier.toISOString().slice(0, 10);
}

/**
 * Date ISO → `MM/AA` pour l'affichage dans la cellule.
 *
 * @param {string|Date|null|undefined} iso
 * @returns {string} `MM/AA`, ou "" (y compris pour la sentinelle, qui n'est pas
 *          une date à montrer).
 */
export function isoToMmaa(iso) {
  if (!iso) return "";
  if (estPeremptionSentinelle(iso)) return "";
  const s = iso instanceof Date ? iso.toISOString().slice(0, 10) : String(iso).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return "";
  return `${m[2]}/${m[1].slice(2)}`;
}

/**
 * Rendu lisible d'une péremption stockée, partout où on l'affiche hors saisie.
 *
 * @param {string|Date|null|undefined} iso
 * @param {(d: any) => string} formatDate  le formateur de dates de l'app
 * @returns {string}
 */
export function formatPeremption(iso, formatDate) {
  if (!iso) return PEREMPTION_SANS_PLACEHOLDER;
  if (estPeremptionSentinelle(iso)) return PEREMPTION_SANS_LIBELLE;
  return typeof formatDate === "function" ? formatDate(iso) : String(iso).slice(0, 10);
}

/**
 * La saisie est-elle complète et cohérente ?
 *
 * @param {string} mmaa
 * @returns {boolean}
 */
export function mmaaEstValide(mmaa) {
  return mmaaToIso(mmaa) !== "";
}
